// THE fetch for our Cloud Functions (ADR-0020). It alone attaches the
// caller's Firebase ID token as `Authorization: Bearer <token>`; the server
// takes the Viewer from that token, so no service sends a `uid` of its own and
// no service builds an Authorization header itself (an ESLint rule in
// eslint.config.js pins that).
//
// Two ways to decide the bearer:
// - by default it gets the token, waiting for auth to initialize, so a request
//   on first paint carries it instead of drawing a 401;
// - a caller passes `token`, used as given. The auth store passes the token it
//   holds; a request whose endpoint never reads the Viewer (the binder page,
//   sync, a public card search) passes `null` and sends none, so the edge has
//   no token to verify.
//
// Same contract as fetch otherwise: resolves to the Response, rejects on a
// network failure. Failing to GET a token never blocks the request: it goes
// without one and the server answers per the endpoint's Need. A `json` init
// field is sent as the JSON body (serialized, with its Content-Type).
//
// Two readers sit on top, one per service contract:
// - apiJson: the parsed body, or a thrown Error unless it reports `success`.
// - apiResult: an ApiResult that never rejects (`ok`, `status`, and the
//   body's `data` / `error`).
//
// The auth store is imported lazily: it imports profile-api.js, which uses
// this helper, so a static import would form a module cycle.

/** @typedef {import('../types.js').FetchLike} FetchLike */
/** @typedef {{ method?: string, headers?: Record<string, string>, body?: string, json?: unknown }} ApiInit */

/**
 * @typedef {Object} ApiFetchOptions
 * @property {string | null} [token] the bearer to send as given (`null`: none)
 * @property {FetchLike} [fetchFn] an injected fetch (tests)
 */

/** @returns {Promise<string | null>} */
async function currentToken() {
  try {
    const store = await import('../stores/auth.svelte.js');
    return (await store.getIdToken()) || null;
  } catch (e) {
    console.warn('[ApiFetch] Could not get an ID token; sending without one:', e);
    return null;
  }
}

/**
 * @param {string} url
 * @param {ApiInit} [init]
 * @param {ApiFetchOptions} [options]
 * @returns {Promise<Response>}
 */
export async function apiFetch(url, init = {}, { token, fetchFn } = {}) {
  const bearer = token !== undefined ? token : await currentToken();
  const { json, ...rest } = init;
  /** @type {Record<string, string>} */
  const headers = { ...rest.headers };
  if (json !== undefined) {
    headers['Content-Type'] = 'application/json';
    rest.body = JSON.stringify(json);
  }
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  return /** @type {Promise<Response>} */ ((fetchFn ?? fetch)(url, { ...rest, headers }));
}

/**
 * The parsed JSON body of a call that must succeed: throws an Error with the
 * server's `error` (or 'Request failed') unless the body reports `success`.
 * @param {string} url
 * @param {ApiInit} [init]
 * @param {ApiFetchOptions} [options]
 * @returns {Promise<any>}
 */
export async function apiJson(url, init = {}, options = {}) {
  const response = await apiFetch(url, init, options);
  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Request failed');
  }
  return data;
}

/**
 * The call as an ApiResult that never rejects: a network failure resolves to
 * `{ ok: false, status: 0 }`; anything the server answered resolves to its
 * status plus the body's `data` and `error` (an unparseable body counts as
 * empty), so callers branch on `ok` alone and informative failures (a 409
 * carrying `data`) keep their payload.
 * @template T
 * @param {string} url
 * @param {ApiInit} [init]
 * @param {ApiFetchOptions} [options]
 * @returns {Promise<import('../types.js').ApiResult<T>>}
 */
export async function apiResult(url, init = {}, options = {}) {
  try {
    const resp = await apiFetch(url, init, options);
    const body = await resp.json().catch(() => ({}));
    return { ok: resp.ok, status: resp.status, data: body.data, error: body.error };
  } catch {
    return { ok: false, status: 0 };
  }
}
